import React, { useEffect, useState, useCallback } from 'react';
import { Command } from 'cmdk';
import { DialogPortal, DialogOverlay } from '../ui/Dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Waves,
  FolderKanban,
  GitFork,
  Users,
  Settings,
  User,
  Bell,
  Search,
  ArrowRight,
} from 'lucide-react';
import { aiAPI } from '../../services/api';

const NAV_ITEMS = [
  { id: 'nav-org', label: 'Organisation', path: '/', icon: Building2, group: 'Navigate' },
  { id: 'nav-streams', label: 'Streams', path: '/streams', icon: Waves, group: 'Navigate' },
  {
    id: 'nav-projects',
    label: 'Projects',
    path: '/projects',
    icon: FolderKanban,
    group: 'Navigate',
  },
  {
    id: 'nav-repos',
    label: 'Repositories',
    path: '/repositories',
    icon: GitFork,
    group: 'Navigate',
  },
  { id: 'nav-members', label: 'Members', path: '/members', icon: Users, group: 'Navigate' },
  { id: 'nav-profile', label: 'Profile', path: '/profile', icon: User, group: 'Navigate' },
  {
    id: 'nav-notifications',
    label: 'Notifications',
    path: '/notifications',
    icon: Bell,
    group: 'Navigate',
  },
  {
    id: 'nav-settings-org',
    label: 'Organisation Settings',
    path: '/settings/organisation/general',
    icon: Settings,
    group: 'Navigate',
  },
];

export default function CommandPalette({ open, onClose }: any) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [dynamicItems, setDynamicItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchDynamic = useCallback(async (q) => {
    setLoading(true);
    try {
      const data = await aiAPI.scopeCatalog({ q: q || '', limit: 30 }, undefined);
      const items = [
        ...(data.projects || []).map((p) => ({
          id: `project-${p.id}`,
          label: p.label || p.name,
          path: p.streamId ? `/streams/${p.streamId}/projects/${p.id}` : '/projects',
          icon: FolderKanban,
          group: 'Projects',
          meta: p.streamName,
        })),
        ...(data.repositories || []).map((r) => ({
          id: `repo-${r.id}`,
          label: r.label || r.name,
          path: `/repositories/${r.id}`,
          icon: GitFork,
          group: 'Repositories',
        })),
        ...(data.streams || []).map((s) => ({
          id: `stream-${s.id}`,
          label: s.label || s.name,
          path: `/streams/${s.id}`,
          icon: Waves,
          group: 'Streams',
        })),
        ...(data.members || []).map((m) => ({
          id: `member-${m.id}`,
          label: m.label || m.name || m.username,
          path: `/members/${m.id}`,
          icon: Users,
          group: 'Members',
        })),
      ];
      setDynamicItems(items);
    } catch {
      setDynamicItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchDynamic(query), query ? 200 : 0);
    return () => clearTimeout(t);
  }, [open, query, fetchDynamic]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const runItem = (item) => {
    navigate(item.path);
    onClose();
  };

  const groupedDynamic = dynamicItems.reduce<Record<string, any[]>>((acc, item) => {
    if (!acc[item.group]) acc[item.group] = [];
    acc[item.group].push(item);
    return acc;
  }, {});

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => !v && onClose()}>
      {open && (
        <DialogPortal forceMount>
          <DialogOverlay open={open} />
          <DialogPrimitive.Content
            forceMount
            className="fixed left-1/2 top-[15vh] z-50 w-full max-w-xl -translate-x-1/2"
            onEscapeKeyDown={onClose}
            onPointerDownOutside={onClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: -8 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className="overflow-hidden rounded-2xl border border-dark-border bg-dark-surface shadow-2xl"
            >
              <Command
                className="flex flex-col"
                shouldFilter={!query || dynamicItems.length === 0}
                loop
              >
                <div className="flex items-center gap-3 border-b border-dark-border px-4">
                  <Search className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                  <Command.Input
                    value={query}
                    onValueChange={setQuery}
                    placeholder="Search pages, repos, members…"
                    className="flex-1 bg-transparent py-4 text-sm text-slate-100 placeholder-slate-500 focus:outline-none"
                    autoFocus
                  />
                  {loading && (
                    <span className="shrink-0 animate-pulse text-xs text-slate-400">Loading…</span>
                  )}
                  <kbd className="hidden shrink-0 items-center gap-1 rounded border border-dark-border bg-dark-elevated px-2 py-0.5 font-mono text-[11px] text-slate-500 sm:inline-flex">
                    esc
                  </kbd>
                </div>

                <Command.List className="max-h-[420px] overflow-y-auto py-2">
                  <Command.Empty className="py-8 text-center text-sm text-slate-400">
                    No results found.
                  </Command.Empty>

                  {(!query || dynamicItems.length === 0) && (
                    <Command.Group heading="Navigate" className="px-2">
                      {NAV_ITEMS.map((item) => (
                        <PaletteItem key={item.id} item={item} onSelect={() => runItem(item)} />
                      ))}
                    </Command.Group>
                  )}

                  {Object.entries(groupedDynamic).map(([group, items]) => (
                    <Command.Group key={group} heading={group} className="px-2">
                      {items.map((item) => (
                        <PaletteItem key={item.id} item={item} onSelect={() => runItem(item)} />
                      ))}
                    </Command.Group>
                  ))}
                </Command.List>

                <div className="flex items-center gap-4 border-t border-gray-100 px-4 py-2.5 text-[11px] text-slate-400/80">
                  <span>
                    <kbd className="font-mono">↑↓</kbd> navigate
                  </span>
                  <span>
                    <kbd className="font-mono">↵</kbd> open
                  </span>
                  <span>
                    <kbd className="font-mono">esc</kbd> close
                  </span>
                </div>
              </Command>
            </motion.div>
          </DialogPrimitive.Content>
        </DialogPortal>
      )}
    </DialogPrimitive.Root>
  );
}

function PaletteItem({ item, onSelect }: any) {
  const Icon = item.icon;
  return (
    <Command.Item
      value={`${item.group} ${item.label} ${item.meta || ''}`}
      onSelect={onSelect}
      className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-300 transition-colors aria-selected:bg-dark-elevated aria-selected:text-slate-100 aria-selected:text-dark-elevated"
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-dark-elevated aria-selected:bg-slate-400">
        <Icon className="h-4 w-4 text-slate-400" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{item.label}</span>
        {item.meta && <span className="block truncate text-xs text-slate-500">{item.meta}</span>}
      </span>
      <ArrowRight
        className="h-3.5 w-3.5 shrink-0 text-slate-400 aria-selected:text-slate-400"
        aria-hidden
      />
    </Command.Item>
  );
}
